import React, { useState, useCallback } from 'react';
import { Container, Card, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { useDropzone } from 'react-dropzone';
import { Helmet } from 'react-helmet-async';
import ProcessingProgress from './common/ProcessingProgress';
import useProcessingProgress from '../hooks/useProcessingProgress';
import '../styles/components.css';

const FormatConverter = () => {
  const [file, setFile] = useState(null);
  const [targetFormat, setTargetFormat] = useState('json');
  const [dateFormat, setDateFormat] = useState('iso');
  const [error, setError] = useState('');
  
  const {
    progress,
    status,
    isComplete,
    isLoading,
    simulateProgress
  } = useProcessingProgress();

  const dateFormats = [
    { value: 'iso', label: 'ISO (2025-02-12)' },
    { value: 'us', label: 'US (02/12/2025)' },
    { value: 'eu', label: 'UK/EU (12/02/2025)' },
    { value: 'iso-time', label: 'ISO with time (2025-02-12 14:55:30)' },
    { value: 'us-time', label: 'US with time (02/12/2025 14:55:30)' }
  ];

  const formatDate = (date, format) => {
    if (!date || !(date instanceof Date) || isNaN(date)) return '';
    
    try {
      switch (format) {
        case 'iso':
          return date.toISOString().split('T')[0];
        case 'us':
          return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }).format(date);
        case 'eu':
          return new Intl.DateTimeFormat('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          }).format(date);
        case 'iso-time':
          return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
          }).format(date).replace(',', '');
        case 'us-time':
          return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
          }).format(date);
        default:
          return date.toISOString().split('T')[0];
      }
    } catch (err) {
      return '';
    }
  };

  const formatCellValue = (cell, isCSV = false) => {
    if (!cell) return '';

    // For CSV, always return strings
    if (isCSV) {
      switch (cell.type) {
        case ExcelJS.ValueType.Date:
          return formatDate(new Date(cell.value), dateFormat);
        case ExcelJS.ValueType.Formula:
          if (cell.result instanceof Date) {
            return formatDate(cell.result, dateFormat);
          }
          return cell.result ? cell.result.toString() : '';
        default:
          return cell.value ? cell.value.toString() : '';
      }
    }

    // For JSON, maintain proper data types
    switch (cell.type) {
      case ExcelJS.ValueType.Date:
        const date = new Date(cell.value);
        const isMidnight = date.getUTCHours() === 0 && 
                         date.getUTCMinutes() === 0 && 
                         date.getUTCSeconds() === 0 &&
                         date.getUTCMilliseconds() === 0;
        
        if (isMidnight) {
          return date.toISOString().split('T')[0];
        }
        return date.toISOString();
      
      case ExcelJS.ValueType.Number:
        // Return actual number for JSON
        return cell.value;
      
      case ExcelJS.ValueType.Boolean:
        // Return actual boolean for JSON
        return cell.value;
      
      case ExcelJS.ValueType.String:
        return cell.value;
      
      case ExcelJS.ValueType.Formula:
        if (cell.result instanceof Date) {
          const date = cell.result;
          const isMidnight = date.getUTCHours() === 0 && 
                           date.getUTCMinutes() === 0 && 
                           date.getUTCSeconds() === 0 &&
                           date.getUTCMilliseconds() === 0;
          
          if (isMidnight) {
            return date.toISOString().split('T')[0];
          }
          return date.toISOString();
        }
        // Try to parse numbers and booleans from formula results
        if (typeof cell.result === 'number') return cell.result;
        if (typeof cell.result === 'boolean') return cell.result;
        return cell.result ? cell.result.toString() : '';
      
      case ExcelJS.ValueType.Null:
        return null;
      
      default:
        if (cell.value === null || cell.value === undefined) return null;
        if (typeof cell.value === 'number') return cell.value;
        if (typeof cell.value === 'boolean') return cell.value;
        return cell.value.toString();
    }
  };

  const validateExcelFile = async (file) => {
    try {
      // First check if it's an Excel file by extension
      const fileExtension = file.name.split('.').pop().toLowerCase();
      if (!['xlsx', 'xls'].includes(fileExtension)) {
        throw new Error('Please upload an Excel file (.xlsx or .xls)');
      }

      // Read the file
      const buffer = await file.arrayBuffer();
      
      try {
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(buffer);
        
        // Get the first worksheet
        const worksheet = workbook.worksheets[0];
        if (!worksheet) {
          throw new Error('The Excel file must contain at least one worksheet');
        }

        // Validate headers (first row)
        const headerRow = worksheet.getRow(1);
        if (!headerRow) {
          throw new Error('Unable to read the first row of the Excel file');
        }

        const headerValues = headerRow.values;
        // ExcelJS starts arrays at index 1
        const headers = headerValues ? headerValues.slice(1) : [];
        
        if (!headers || headers.length === 0) {
          throw new Error('The Excel file must have at least one column header in the first row');
        }

        // Check for empty or duplicate headers
        const headerSet = new Set();
        for (let i = 0; i < headers.length; i++) {
          const header = headers[i];
          if (!header || header.toString().trim() === '') {
            throw new Error(`Column ${i + 1} is missing a header`);
          }
          if (headerSet.has(header.toString().toLowerCase())) {
            throw new Error(`Duplicate header found: ${header}`);
          }
          headerSet.add(header.toString().toLowerCase());
        }

        // Check for data rows
        if (worksheet.rowCount < 2) {
          throw new Error('The Excel file must contain at least one row of data below the headers');
        }

        // Success - file is valid
        return true;
      } catch (excelError) {
        // If there's an error parsing the Excel file
        if (excelError.message.includes('Invalid')) {
          throw new Error('The file appears to be corrupted or not a valid Excel file');
        }
        throw excelError;
      }
    } catch (err) {
      console.error('Excel validation error:', err);
      setError(err.message);
      setFile(null);
      return false;
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setError('');
      
      try {
        if (await validateExcelFile(file)) {
          setFile(file);
        }
      } catch (err) {
        console.error('Error validating file:', err);
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv']
    },
    maxFiles: 1
  });

  const conversionStages = [
    { progress: 0, status: 'Starting conversion...', duration: 500 },
    { progress: 30, status: 'Reading Excel file...', duration: 700 },
    { progress: 60, status: 'Processing data...', duration: 800 },
    { progress: 90, status: 'Preparing download...', duration: 500 },
    { progress: 100, status: 'Complete!', duration: 500 }
  ];

  const convertFile = async () => {
    if (!file) {
      setError('Please select a file to convert');
      return;
    }

    setError('');
    
    try {
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const workbook = new ExcelJS.Workbook();
          await workbook.xlsx.load(e.target.result);
          
          let buffer;
          let fileName;
          const baseFileName = file.name.split('.')[0];

          switch (targetFormat) {
            case 'json':
              const worksheet = workbook.worksheets[0];
              if (!worksheet) {
                throw new Error('No worksheet found in the Excel file');
              }

              const headerRow = worksheet.getRow(1);
              const headerValues = headerRow.values;
              // Excel row values array is 1-based, so we start from index 1
              const headers = headerValues ? headerValues.slice(1) : [];
              
              // Validate headers
              if (!headers || !headers.length || !headers.every(header => header)) {
                throw new Error('All field labels in the first row must be present');
              }

              const jsonData = [];
              
              // Start from row 2 for data
              for (let rowNumber = 2; rowNumber <= worksheet.rowCount; rowNumber++) {
                const rowData = worksheet.getRow(rowNumber);
                const rowValues = rowData.values;
                
                // Skip if no values or empty row
                if (!rowValues || rowValues.length <= 1) continue;
                
                // Create object from row data
                const rowObject = {};
                headers.forEach((header, index) => {
                  // Excel cells are 1-based, so we add 1 to the index
                  const cell = rowData.getCell(index + 1);
                  rowObject[header] = formatCellValue(cell);
                });
                
                jsonData.push(rowObject);
              }
              
              buffer = new TextEncoder().encode(JSON.stringify(jsonData, null, 2));
              fileName = `${baseFileName}.json`;
              break;

            case 'csv':
              const worksheetCsv = workbook.worksheets[0];
              if (!worksheetCsv) {
                throw new Error('No worksheet found in the Excel file');
              }

              const headerRowCsv = worksheetCsv.getRow(1);
              const headerValuesCsv = headerRowCsv.values;
              // Excel row values array is 1-based, so we start from index 1
              const headersCsv = headerValuesCsv ? headerValuesCsv.slice(1) : [];
              
              // Validate headers
              if (!headersCsv || !headersCsv.length || !headersCsv.every(header => header)) {
                throw new Error('All field labels in the first row must be present');
              }

              const csvData = [];
              // Add headers as first row
              csvData.push(headersCsv.join(','));
              
              // Process data rows starting from row 2
              for (let rowNumber = 2; rowNumber <= worksheetCsv.rowCount; rowNumber++) {
                const rowData = worksheetCsv.getRow(rowNumber);
                const rowValues = rowData.values;
                
                // Skip if no values or empty row
                if (!rowValues || rowValues.length <= 1) continue;
                
                // Process each cell in the row
                const processedRow = headersCsv.map((_, index) => {
                  // Excel cells are 1-based, so we add 1 to the index
                  const cell = rowData.getCell(index + 1);
                  const value = formatCellValue(cell, true);
                  
                  // If the value contains commas, quotes, or newlines, wrap it in quotes and escape existing quotes
                  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
                    return `"${value.replace(/"/g, '""')}"`;
                  }
                  return value;
                });
                
                csvData.push(processedRow.join(','));
              }
              
              buffer = new TextEncoder().encode(csvData.join('\n'));
              fileName = `${baseFileName}.csv`;
              break;

            default:
              throw new Error('Unsupported format');
          }

          await simulateProgress(conversionStages);
          saveAs(new Blob([buffer]), fileName);
          setFile(null);
        } catch (err) {
          throw err;
        }
      };

      reader.onerror = () => {
        throw reader.error;
      };

      reader.readAsArrayBuffer(file);
    } catch (err) {
      setError('Error converting file: ' + err.message);
    }
  };

  return (
    <Container className="tool-container">
      <Helmet>
        <title>Excel to JSON/CSV - Free File Format Converter | Excel Toolbox</title>
        <meta name="description" content="Free tool to convert Excel files to JSON or CSV format. Choose custom date formats and process files securely on your device without uploading data." />
        <meta name="keywords" content="excel to json, excel to csv, convert excel, json converter, csv converter, excel converter" />
        <meta property="og:title" content="Excel to JSON/CSV - Free File Format Converter" />
        <meta property="og:description" content="Convert Excel files to JSON or CSV format with customizable options. Process files securely on your local device." />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://exceltoolbox.org/excel-to-json-csv" />
      </Helmet>

      <Card className="tool-card">
        <Card.Header as="h5" className="tool-header">Excel to JSON/CSV</Card.Header>
        <Card.Body className="tool-body">
          <Row>
            <Col>
              <Card.Text className="mb-4">
                Convert Excel files (XLSX or XLS) to JSON or CSV format. The converter will preserve data types and formatting while creating clean, structured output files.
                <br /><br />
                <strong>Required Excel Format:</strong>
                <ul className="mb-0">
                  <li>First row must contain unique column headers</li>
                  <li>Each column must have a header (no empty columns)</li>
                  <li>Data should start from row 2</li>                
                </ul>
              </Card.Text>

              <div className="dropzone-container">
                <div {...getRootProps()}>
                  <input {...getInputProps()} />
                  <div className={`dropzone ${isDragActive ? 'active' : ''}`}>
                    <i className="bi bi-cloud-upload dropzone-icon"></i>
                    {file ? (
                      <p className="dropzone-text">Selected file: {file.name}</p>
                    ) : (
                      <p className="dropzone-text">
                        {isDragActive ? 'Drop the file here' : 'Drag and drop an Excel file here, or click to select'}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <Form.Group className="mb-3">
                <Form.Label>Target Format</Form.Label>
                <div>
                  <Form.Check
                    inline
                    type="radio"
                    id="json-format"
                    label="JSON"
                    value="json"
                    checked={targetFormat === 'json'}
                    onChange={(e) => setTargetFormat(e.target.value)}
                    disabled={isLoading}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    id="csv-format"
                    label="CSV"
                    value="csv"
                    checked={targetFormat === 'csv'}
                    onChange={(e) => setTargetFormat(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
              </Form.Group>

              {targetFormat === 'csv' && (
                <Form.Group className="mb-3">
                  <Form.Label>Date Format</Form.Label>
                  <Form.Select
                    value={dateFormat}
                    onChange={(e) => setDateFormat(e.target.value)}
                    disabled={isLoading}
                  >
                    {dateFormats.map(format => (
                      <option key={format.value} value={format.value}>
                        {format.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              )}

              {error && <Alert variant="danger">{error}</Alert>}

              <ProcessingProgress
                progress={progress}
                status={status}
                isComplete={isComplete}
                isLoading={isLoading}
                completionMessage="File conversion complete! Your download should start automatically."
              />

              <Button
                variant="success"
                onClick={convertFile}
                disabled={isLoading || !file}
                className="action-button"
              >
                {isLoading ? 'Converting...' : 'Convert File'}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default FormatConverter;
